import { useState, useEffect } from 'react'
import { Bot, Save, Loader2, Power } from 'lucide-react'

import { Button } from '@/common/ui/button'
import { Textarea } from '@/common/ui/textarea'
import { Switch } from '@/common/ui/switch'
import { Label } from '@/common/ui/label'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'

import {
	useQueryAiSetting,
	useMutationUpdateAiSetting,
} from '../../../api/hooks'

export function ChatSettingsAiTab() {
	const [context, setContext] = useState('')
	const [aiEnabled, setAiEnabled] = useState(true)

	const { data, isLoading } = useQueryAiSetting()
	const saveMutation = useMutationUpdateAiSetting()

	useEffect(() => {
		if (data) {
			setContext(data.systemContext)
			setAiEnabled(data.aiEnabled ?? true)
		}
	}, [data])

	const handleSave = () => {
		saveMutation.mutate({ systemContext: context, aiEnabled })
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		)
	}

	const isDirty =
		context !== (data?.systemContext ?? '') ||
		aiEnabled !== (data?.aiEnabled ?? true)

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<div className="flex items-center gap-2">
						<Bot className="h-5 w-5 text-primary" />
						<CardTitle>บริบทระบบของ AI (System Context)</CardTitle>
					</div>
					<CardDescription>
						แอดมินสามารถใส่บริบทเฉพาะเว็บนี้เพื่อปรับการทำงานของ AI ได้
						<br />
						<span className="text-xs text-muted-foreground">
							ส่วนนี้จะถูกใส่เป็น &quot;ADMIN-CUSTOMIZED RULES&quot; ใน prompt
							ของ AI โดยจะ override default behavior เมื่อมีกฎที่ขัดแย้งกัน —
							ใส่เฉพาะกฎ/นโยบายเฉพาะเว็บที่ AI ต้องรู้ (เช่น ขั้นต่ำฝาก/ถอน,
							น้ำเสียง, ข้อห้ามเฉพาะ)
						</span>
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex items-center justify-between rounded-lg border p-4">
						<div className="flex items-center gap-3">
							<Power className="h-5 w-5 text-muted-foreground" />
							<div>
								<Label htmlFor="ai-enabled" className="text-base font-medium">
									เปิดใช้งาน AI
								</Label>
								<p className="text-sm text-muted-foreground">
									ปิดเพื่อปิด AI ทั้งหมด แชทจะกลับไปเหมือนก่อนมี AI
								</p>
							</div>
						</div>
						<Switch
							id="ai-enabled"
							checked={aiEnabled}
							onCheckedChange={setAiEnabled}
						/>
					</div>

					<Textarea
						value={context}
						onChange={(e) => setContext(e.target.value)}
						placeholder="ใส่บริบทเฉพาะของเว็บที่ AI ต้องรู้ เช่น ขั้นต่ำฝาก 100 บาท, น้ำเสียงพนักงานหญิง, ห้ามรับปากเรื่องเงิน ..."
						className="min-h-[400px] font-mono text-sm"
					/>
				</CardContent>
			</Card>

			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					{isDirty
						? 'มีการเปลี่ยนแปลง — กดบันทึกเพื่อใช้งาน'
						: 'ไม่มีการเปลี่ยนแปลง'}
				</p>
				<Button
					onClick={handleSave}
					disabled={!isDirty || saveMutation.isPending}
				>
					{saveMutation.isPending ? (
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					) : (
						<Save className="mr-2 h-4 w-4" />
					)}
					บันทึก
				</Button>
			</div>
		</div>
	)
}
